import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { KnowledgeSpaceHeader } from './KnowledgeSpaceHeader';
import { OverviewTab } from './overview/OverviewTab';
import { ChevronLeftIcon, AlertCircleIcon, RefreshCwIcon } from 'lucide-react';
import { DocumentsTab } from './documents/DocumentsTab';
import { ChatbotsTab } from './chatbots/ChatbotsTab';
import { ExternalApisTab } from './apis/ExternalApisTab';
import { SettingsTab } from './settings/SettingsTab';
import { useErrorBoundary } from '../../hooks/useErrorBoundary';
import { Outlet } from 'react-router-dom';
type Tab = 'overview' | 'documents' | 'chatbots' | 'apis' | 'settings';
export const KnowledgeSpaceDetail = () => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [{
    hasError,
    error
  }, handleError, resetError] = useErrorBoundary();
  const [isLoading, setIsLoading] = useState(true);
  const [spaceData, setSpaceData] = useState<any>(null);
  useEffect(() => {
    const loadSpaceData = async () => {
      try {
        setIsLoading(true);
        // Simulate API call
        if (!id) throw new Error('Invalid Knowledge Space ID');
        // In real implementation, this would be an API call
        const data = {
          id: id,
          name: 'ADHD Research Space',
          chatbotCount: 3,
          storageUsed: '1.2 GB',
          documentsCount: 45,
          apiConnections: 2,
          lastUpdated: '2025-04-28T10:30:00Z',
          createdAt: '2025-04-27T15:30:00Z',
          updatedAt: '2025-04-28T10:30:00Z',
          description: 'AI-powered research assistant for ADHD studies and papers',
          status: 'active' as const,
          chatbots: [{
            id: '1',
            name: 'Research Assistant',
            description: 'Analyzes and synthesizes ADHD research papers',
            connectedSpaces: 3,
            totalInteractions: 1250,
            status: 'active' as const,
            type: 'researcher' as const,
            lastActive: '2025-04-28T10:30:00Z'
          }, {
            id: '2',
            name: 'Patient Support',
            description: 'Answers patient questions about ADHD',
            connectedSpaces: 2,
            totalInteractions: 3420,
            status: 'active' as const,
            type: 'assistant' as const,
            lastActive: '2025-04-28T11:15:00Z'
          }, {
            id: '3',
            name: 'Data Analyst',
            description: 'Analyzes research data and generates insights',
            connectedSpaces: 1,
            totalInteractions: 850,
            status: 'active' as const,
            type: 'analyst' as const,
            lastActive: '2025-04-28T09:45:00Z'
          }]
        };
        setSpaceData(data);
      } catch (error) {
        handleError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSpaceData();
  }, [id, handleError]);
  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/dashboard');
  };
  if (hasError) {
    return <div className="min-h-screen bg-surface flex items-center justify-center p-4" data-id="element-1155">
        <div className="bg-surface-light border border-surface-border rounded-none p-6 max-w-lg w-full" data-id="element-1156">
          <div className="flex items-start gap-4" data-id="element-1157">
            <div className="p-3 bg-status-error/10 rounded-none" data-id="element-1158">
              <AlertCircleIcon size={24} className="text-status-error" data-id="element-1159" />
            </div>
            <div className="flex-1" data-id="element-1160">
              <h1 className="text-lg font-medium text-white font-mono mb-2" data-id="element-1161">
                Failed to load Knowledge Space
              </h1>
              <div className="bg-surface p-4 rounded-none border border-surface-border mb-4" data-id="element-1162">
                <code className="text-sm text-gray-400 font-mono" data-id="element-1163">
                  {error?.message || 'An unexpected error occurred'}
                </code>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-1164">
                <button onClick={resetError} className="flex items-center justify-center gap-2 px-4 py-2 bg-accent 
                           hover:bg-accent-light text-white rounded-none transition-colors" data-id="element-1165">
                  <RefreshCwIcon size={16} data-id="element-1166" />
                  <span data-id="element-1167">Try Again</span>
                </button>
                <button onClick={() => navigate('/dashboard')} className="flex items-center justify-center gap-2 px-4 py-2 border 
                           border-surface-border text-gray-400 hover:text-white 
                           rounded-none transition-colors" data-id="element-1168">
                  <ChevronLeftIcon size={16} data-id="element-1169" />
                  <span data-id="element-1170">Return to Dashboard</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  if (isLoading) {
    return <div className="min-h-screen bg-surface flex items-center justify-center" data-id="element-1171">
        <div className="flex flex-col items-center gap-4" data-id="element-1172">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" data-id="element-1173" />
          <p className="text-gray-400 font-mono" data-id="element-1174">Loading Knowledge Space...</p>
        </div>
      </div>;
  }
  if (!spaceData) {
    return <Navigate to="/dashboard" replace data-id="element-1175" />;
  }
  return <div className="flex flex-col min-h-[calc(100vh-theme(spacing.16))]" data-id="element-1176">
      <div className="bg-surface-light border-b border-surface-border" data-id="element-1177">
        <div className="max-w-7xl mx-auto" data-id="element-1178">
          <div className="px-2 sm:px-6 py-4" data-id="element-1179">
            <button onClick={handleBackClick} className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors" data-id="element-1180">
              <ChevronLeftIcon size={16} data-id="element-1181" />
              <span className="font-mono" data-id="element-1182">Back to Knowledge Spaces</span>
            </button>
          </div>
          <KnowledgeSpaceHeader activeTab={activeTab} onTabChange={setActiveTab} spaceName={spaceData.name} data-id="element-1183" />
        </div>
      </div>
      <div className="flex-1 bg-surface" data-id="element-1184">
        <main className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-8" data-id="element-1185">
          {activeTab === 'overview' && <OverviewTab spaceData={spaceData} data-id="element-1186" />}
          {activeTab === 'documents' && <DocumentsTab data-id="element-1187" />}
          {activeTab === 'chatbots' && <ChatbotsTab spaceId={id || ''} chatbots={spaceData.chatbots} showCreateButton={true} data-id="element-1188" />}
          {activeTab === 'apis' && <ExternalApisTab data-id="element-1189" />}
          {activeTab === 'settings' && <SettingsTab spaceId={id || ''} data-id="element-1190" />}
        </main>
      </div>
      {/* Add Outlet for nested routes */}
      <Outlet data-id="element-1191" />
    </div>;
};